/**
 * California waste-code field-issue rules.
 *
 * Separate from `stateWasteCodes.ts` — that file is the canonical code *data*,
 * already several times over the file-length budget, and these are behavior.
 * The Texas rules still live there for historical reasons.
 *
 * Both rules here are set-level: one asks whether a CA code is present at all,
 * the other how many there are. That is the shape difference from the Texas
 * rules, which each judge a single code.
 *
 * The two rules do not rest on equally firm ground, which is why they carry
 * different severities:
 *
 *  - **Requiring a CA code is codified.** 22 CCR § 66262.23(a)(1) obliges the
 *    generator to complete the manifest per the EPA Form 8700-22 instructions,
 *    whose Item 13 requires that "State waste codes that are not redundant with
 *    federal codes must be entered here"; § 66262.23(a)(6) points at the CA code
 *    list. The 2005 federal manifest rule (70 FR 10776) is explicit that "there
 *    is no discretion to omit such state codes." DTSC cites the omission as
 *    inspection violation GR05.
 *
 *    The obligation attaches to *all* California hazardous waste, not only
 *    federally-coded waste — § 66262.23(a) reaches "any hazardous or extremely
 *    hazardous waste", and only the EPA number is qualified "if applicable". So
 *    the trigger is evidence of hazardous waste, not federal-code presence; see
 *    {@link hasHazardousWasteEvidence}, which also records the gap this leaves.
 *    Note the converse is never true: a
 *    non-RCRA California waste legitimately carries a CA code and no federal
 *    code (used oil 221, asbestos 151, treated wood 614), so a missing federal
 *    code is never flagged.
 *
 *    "No applicable code" is not a defence either — Appendix XII carries
 *    catch-alls (123, 135, 181, 214, 223, 343, 352, 491).
 *
 *  - **Requiring exactly one CA code is DTSC's stated expectation, not
 *    regulation.** DTSC says it in three places — the Box 13 manifest FAQ
 *    ("Only one California waste code… Only multiple RCRA waste codes are
 *    allowed"), the manifest-changes fact sheet, and the HWTS data spec ("Only
 *    list 1 of this code") — but no CCR provision forbids a second code.
 *    Appendix XII(a)(2) supplies only a precedence rule (a "California
 *    Restricted Wastes" code wins over another category's), not a count limit.
 *    So this fires as advice, worded as DTSC's expectation rather than as an
 *    asserted violation.
 */
import { normalizeStateWasteCode } from "./stateWasteCodeNormalization";
import { isValidStateWasteCode } from "./stateWasteCodes";

export type CaWasteCodeRuleKey = "CA_HAZARDOUS_NO_CA_CODE" | "CA_MULTIPLE_CA_CODES";

export type CaWasteCodeRuleContext = {
  /**
   * Whether this source's own waste line carries a real (sentinel-stripped)
   * federal code. Unlike the TX rules, which read a line-level union across
   * sources, this is strictly per-source: a vendor line's federal codes say
   * nothing about whether the eManifest line needed a CA code.
   */
  hasFederalCode: boolean;
  /**
   * Whether this source carries any real (sentinel-stripped) state waste code.
   * Evaluated alongside a zero CA-code count, so in practice this means "a
   * state code that is not California's" — itself evidence the line is
   * regulated waste that should have carried a CA code too.
   *
   * Computed by the caller because the sentinel list lives in
   * `encamp-waste-data-access`, which depends on this package.
   */
  hasStateCode: boolean;
};

/**
 * Whether anything on this source's own waste line says "this is regulated
 * waste" independently of a California code.
 *
 * Only codes count, and only codes actually written on that source. Two
 * tempting signals are deliberately excluded, both because they mark lines that
 * have no waste-code data at all — leaving a reviewer nothing to fix:
 *
 *  - **DOT hazardous.** The most common signal in the data (215 of 373
 *    otherwise-code-free California lines on `release`), but DOT hazardous is
 *    not a RCRA or California hazardous-waste determination. Non-RCRA hazardous
 *    materials — lithium batteries, aerosols, environmentally hazardous
 *    substances — correctly carry a DOT description and no waste code. Treating
 *    it as evidence is what forced the reverse direction of the box-13/box-9
 *    check to be withdrawn after it produced thousands of unresolvable flags.
 *  - **A hazardous `vendorClassification` override.** It is our own
 *    pipeline-derived stamp, not something the generator wrote on a manifest.
 *    Measured on `release` it would have fired on 3,768 lines carrying no waste
 *    codes at all — and every one of them is universal waste (3,459 batteries,
 *    236 lamps, 61 aerosol cans, 12 mercury devices), which 22 CCR § 66261.9(a)
 *    exempts from the ch. 6.5 implementing regulations and §§ 66273.38-39 let
 *    ship on a log, invoice or bill of lading rather than a manifest. Those
 *    lines carry no waste codes because they are not manifested hazardous
 *    waste, not because a code is missing.
 *
 * Note that universal waste being hazardous waste under reduced management
 * standards is not a contradiction with it being unmanifested and uncoded — the
 * three facts are consistent, and the accompanying `willNotReport` stamp is a
 * symptom of the exemption rather than independent evidence.
 *
 * The consequence is a known, accepted gap: a California-only hazardous line
 * that carries no code whatsoever is a real violation we cannot detect, because
 * nothing trustworthy in the record distinguishes it from a line that is not
 * subject to manifesting at all.
 *
 * `willNotReport` is deliberately NOT used to suppress this rule. It governs
 * biennial reporting and generator status, not manifest completeness: a
 * federally-coded hazardous line on a manifest needs a CA code whether or not we
 * roll it into a report. It overlaps 290 of the 556 rule firings on `release`,
 * so suppressing on it would hide over half of them behind a flag that means
 * something else.
 */
function hasHazardousWasteEvidence(ctx: CaWasteCodeRuleContext): boolean {
  return ctx.hasFederalCode || ctx.hasStateCode;
}

/**
 * Unlike the TX rules, which each answer "is *this code* wrong?", both CA rules
 * answer "is *this set of codes* wrong?" — one is an absence check, the other a
 * cardinality check. So the predicates take the whole set and
 * {@link runCaStateWasteCodeRules} has no per-code loop.
 *
 * Callers pass codes that already exist on a source. A source with no waste
 * line at all must not be evaluated: `CA_HAZARDOUS_NO_CA_CODE` fires on
 * absence, so an absent source would otherwise look like a violating one.
 */
export const CA_STATE_WASTE_CODE_RULES: Record<
  CaWasteCodeRuleKey,
  (codes: string[], ctx: CaWasteCodeRuleContext) => boolean
> = {
  CA_HAZARDOUS_NO_CA_CODE: (codes, ctx) =>
    hasHazardousWasteEvidence(ctx) && caCodesIn(codes).length === 0,
  CA_MULTIPLE_CA_CODES: (codes) => caCodesIn(codes).length > 1,
};

/** Human-readable description per rule key (shown in the Field Issues UI). */
export const CA_STATE_WASTE_CODE_RULE_DESC: Record<CaWasteCodeRuleKey, string> = {
  CA_HAZARDOUS_NO_CA_CODE:
    "Hazardous waste with no California waste code — CA requires one on every hazardous waste line",
  CA_MULTIPLE_CA_CODES:
    "More than one California waste code — DTSC expects a single CA code per waste line",
};

const CA_RULE_KEYS = Object.keys(CA_STATE_WASTE_CODE_RULES) as CaWasteCodeRuleKey[];

/**
 * The valid CA codes within `codes`, deduped, preserving input order.
 *
 * Membership in DTSC's Appendix XII list, not the three-digit shape: a
 * three-digit string that is not a real CA code does not satisfy the
 * requirement. (`manifestComparisonShared.ts` keeps a shape-only
 * `CA_WASTE_CODE_PATTERN` for display grouping — deliberately not reused here,
 * because the two answer different questions.)
 */
function caCodesIn(codes: string[]): string[] {
  const seen = new Set<string>();
  const found: string[] = [];
  for (const code of codes) {
    // Deduped on the canonical form, not the raw string: `141` and `CA141` are
    // the same code, and counting them separately fired the cardinality rule on
    // a compliant line. The pipeline happens to normalize first, but this module
    // is exported with no such precondition.
    const canonical = normalizeStateWasteCode(code);
    if (canonical === null || !isValidStateWasteCode(canonical, "CA")) continue;
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    found.push(canonical);
  }
  return found;
}

/**
 * Runs every CA rule over the source's code set, returning one entry per firing.
 *
 * `codes` carries the offending values for display. The cardinality rule names
 * the CA codes it counted; the absence rule has no offending value by
 * definition, so it returns an empty array and consumers attribute the codes
 * that triggered the requirement instead — this source's federal and state
 * codes. They must attribute something: an all-null row cannot register a
 * material change, so it could never re-open once auto-ignored.
 */
export function runCaStateWasteCodeRules(
  stateCodes: string[],
  ctx: CaWasteCodeRuleContext
): Array<{ ruleKey: CaWasteCodeRuleKey; codes: string[] }> {
  const fired: Array<{ ruleKey: CaWasteCodeRuleKey; codes: string[] }> = [];
  for (const ruleKey of CA_RULE_KEYS) {
    if (!CA_STATE_WASTE_CODE_RULES[ruleKey](stateCodes, ctx)) continue;
    fired.push({
      ruleKey,
      codes: ruleKey === "CA_MULTIPLE_CA_CODES" ? caCodesIn(stateCodes) : [],
    });
  }
  return fired;
}
